import { getNewsByFilter } from "../../api/apiHandler";
import React, { useEffect, useState } from "react";
import BreakingNews from "../../components/BreakingNews";
import RightSidebar from "../../components/RightSidebar";
import TopAdBanner from "../../components/TopAdBanner";
import LeftAdBanner from "../../components/LeftAdBanner";
import WeatherWidget from "../../components/WeatherWidget";
import logo from "../../Assets/images/mlogo.png";
import { getMediaBaseUrl } from "../../utils/backendUrl";

const getEmbedUrl = (url) => {
  if (!url) return "";

  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  if (match) return `https://www.youtube.com/embed/${match[1]}`;

  return url.includes("youtube.com/") || url.includes("youtu.be") ? "" : url;
};

const Videos = () => {
  const [allVideos, setAllVideos] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const PER_PAGE = 9;

  const BASE_URL = getMediaBaseUrl();

  useEffect(() => {
    let isMounted = true;
    const loadVideos = async () => {
      try {
        const res = await getNewsByFilter({ category: "video news", limit: 30 });
        let items = Array.isArray(res?.data) ? res.data : [];

        if (items.length === 0) {
          const resVideo = await getNewsByFilter({ has_video: "1", limit: 30 });
          items = Array.isArray(resVideo?.data) ? resVideo.data : [];
        }

        if (isMounted) {
          const mapped = items
            .map((v) => {
              let imageUrl = "";
              if (v.thumbnail) {
                if (v.thumbnail.startsWith("http")) {
                  imageUrl = v.thumbnail;
                } else if (v.thumbnail.startsWith("/images/")) {
                  imageUrl = v.thumbnail;
                } else {
                  imageUrl = `${BASE_URL}/uploads/news/${encodeURIComponent(v.thumbnail)}`;
                }
              }
              return {
                id: v.id,
                title: v.news_title || v.title || "",
                category: v.category || "",
                state: v.state || "",
                date: v.createdAt ? new Date(v.createdAt).toLocaleDateString("hi-IN") : "",
                url: getEmbedUrl(v.youtube_url || ""),
                image: imageUrl,
                link: `/news/${v.id}`,
              };
            })
            .filter((v) => v.url !== "");
          setAllVideos(mapped);
          setActiveIndex(0);
        }
      } catch (err) {
        console.error("Error fetching videos:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadVideos();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeVideo = allVideos[activeIndex];

  const totalPages = Math.max(1, Math.ceil(allVideos.length / PER_PAGE));
  const safePage = Math.min(page, totalPages - 1);
  const pageVideos = allVideos.slice(safePage * PER_PAGE, safePage * PER_PAGE + PER_PAGE);

  const handleSelectVideo = (index) => {
    setActiveIndex(index);
    setPage(Math.floor(index / PER_PAGE));
  };

  const handlePrevPage = () => {
    if (safePage > 0) {
      setPage(safePage - 1);
      setActiveIndex((safePage - 1) * PER_PAGE);
    }
  };

  const handleNextPage = () => {
    if (safePage < totalPages - 1) {
      setPage(safePage + 1);
      setActiveIndex(safePage * PER_PAGE);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen pb-12">
      {/* ================= PREMIUM CATEGORY BANNER ================= */}
      <div className="bg-gradient-to-br from-[#2d006b] to-[#4600a3] text-white py-6 md:py-8 mb-6 relative overflow-hidden shadow-inner">
        {/* Subtle Background Patterns */}
        <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjEiIGZpbGw9InJnYmEoMCwwLDAsMC4wNSkiLz48L3N2Zz4=')]" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-white opacity-10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 animate-pulse" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-brand-red opacity-10 rounded-full blur-2xl translate-y-1/3 -translate-x-1/4" />

        <div className="max-w-7xl mx-auto px-4 relative z-10 flex flex-col items-center text-center">
          <span className="text-purple-200 text-xs md:text-sm font-bold tracking-[0.2em] uppercase mb-1">विशेष कवरेज</span>
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-black capitalize tracking-tight mb-3 drop-shadow-md">
            वीडियो गैलरी
          </h1>
          <div className="w-12 h-1 bg-gradient-to-r from-purple-200 to-white rounded-full opacity-90 shadow-[0_0_15px_rgba(255,255,255,0.5)]" />
        </div>
      </div>

      <TopAdBanner />

      <div className="max-w-[1400px] mx-auto px-4">
        <BreakingNews />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-4">
          {/* LEFT SIDEBAR (Ad + Weather) */}
          <div className="hidden lg:block lg:col-span-2">
            <div className="space-y-3 sticky top-[70px]">
              <LeftAdBanner />
              <div>
                <WeatherWidget defaultCity="खजुराहो" />
              </div>
            </div>
          </div>

          {/* MIDDLE CONTENT */}
          <div className="lg:col-span-8">
            {loading && (
              <div className="py-16 text-center">
                <p className="text-gray-500 font-semibold">वीडियो लोड हो रहे हैं...</p>
              </div>
            )}

            {!loading && allVideos.length === 0 && (
              <div className="py-16 text-center">
                <p className="text-gray-500 font-semibold">अभी कोई वीडियो उपलब्ध नहीं है।</p>
              </div>
            )}

            {/* MAIN FEATURED PLAYER */}
            {!loading && activeVideo && (
              <div className="mb-8 bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                <div className="relative w-full aspect-video bg-brand-red">
                  <iframe
                    key={activeVideo.id}
                    src={activeVideo.url}
                    title={activeVideo.title}
                    className="absolute inset-0 w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
                <div className="p-4">
                  <span className="inline-block bg-brand-red text-white text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded mb-2">
                    {activeVideo.category}
                  </span>
                  <h2 className="text-lg md:text-xl font-bold text-gray-900 leading-snug">
                    {activeVideo.title}
                  </h2>
                  <p className="text-gray-400 text-xs mt-1">
                    {activeVideo.state ? `${activeVideo.state} • ` : ""}{activeVideo.date}
                  </p>
                </div>
              </div>
            )}

            {/* VIDEO GRID */}
            {!loading && allVideos.length > 0 && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl md:text-2xl font-black text-gray-900 uppercase tracking-wide">
                    वीडियो सूची
                  </h2>
                  <span className="text-sm text-gray-400 font-semibold">
                    {(activeIndex + 1)} / {allVideos.length}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {pageVideos.map((video) => {
                    const index = allVideos.indexOf(video);
                    let imageUrl = video.image || logo;
                    return (
                      <button
                        key={video.id}
                        onClick={() => handleSelectVideo(index)}
                        className={`text-left bg-white rounded-xl overflow-hidden shadow-sm border transition hover:-translate-y-1 hover:shadow-md cursor-pointer ${
                          index === activeIndex ? "ring-2 ring-brand-red" : "border-gray-200"
                        }`}
                      >
                        <div className="relative w-full aspect-video bg-gray-900 overflow-hidden">
                          <img
                            src={imageUrl}
                            alt={video.title}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-black/30 flex items-center justify-center transition group-hover:bg-black/20">
                            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-brand-red/90 text-white shadow-lg">
                              <i className="fa-solid fa-play ml-0.5"></i>
                            </div>
                          </div>
                          {index === activeIndex && (
                            <div className="absolute top-2 left-2 bg-brand-red text-white text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded shadow">
                              चल रहा है
                            </div>
                          )}
                        </div>

                        <div className="p-3">
                          <span className="inline-block bg-purple-100 text-purple-700 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded mb-1.5">
                            {video.category}
                          </span>
                          <h3 className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2">
                            {video.title}
                          </h3>
                          <p className="text-gray-400 text-[11px] mt-1">{video.date}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* NEXT / PREVIOUS PAGINATION */}
                <div className="mt-8 flex items-center justify-center gap-4">
                  <button
                    onClick={handlePrevPage}
                    disabled={safePage === 0}
                    className="flex items-center gap-2 bg-purple-700 text-white font-bold py-2.5 px-6 rounded-full shadow-md transition hover:scale-105 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <i className="fa-solid fa-chevron-left"></i> पिछला
                  </button>
                  <span className="text-sm font-bold text-gray-500">
                    पेज {safePage + 1} / {totalPages}
                  </span>
                  <button
                    onClick={handleNextPage}
                    disabled={safePage >= totalPages - 1}
                    className="flex items-center gap-2 bg-purple-700 text-white font-bold py-2.5 px-6 rounded-full shadow-md transition hover:scale-105 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    अगला <i className="fa-solid fa-chevron-right"></i>
                  </button>
                </div>
              </>
            )}
          </div>

          {/* RIGHT SIDEBAR */}
          <aside className="lg:col-span-2">
            <RightSidebar />
          </aside>
        </div>
      </div>
    </div>
  );
};

export default Videos;